import { MRT_RowData } from 'material-react-table'
import React, { useEffect, useMemo, useState } from 'react'

import { TextArea } from '@/components/UberForm'
import { useAppContext } from '@/hooks'
import { cn } from '@/utils/cn'

import { ModalForm } from '../../Modal/ModalForm'
import { type MrtCellProps } from '../types'
import { CellTooltip } from './CellTooltip'
import { CopyTextButton } from './CopyTextButton'
import { MultilinePreview } from './MultilinePreview'

const MIN_TOOLTIP_LENGTH = 13

export const TextModalCell = <TData extends MRT_RowData>({
	cell,
	row,
	column,
	updateCellValue,
	isDisabled = false,
}: MrtCellProps<TData>) => {
	const { t } = useAppContext()
	const [isModalOpen, setModalOpen] = useState(false)
	const [textAreaValue, setTextAreaValue] = useState<string>('')

	const cellValue = cell?.getValue()

	const onSubmit = () => {
		setModalOpen(false)
		updateCellValue?.(
			row?.index as number,
			column?.id as string,
			textAreaValue?.replace(/\n/g, ' '),
		)
	}

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.metaKey) {
			e.preventDefault()
			onSubmit()
		}
	}

	const className = cn('h-full w-full py-2', {
		'text-black': isDisabled,
	})

	useEffect(() => {
		setTextAreaValue(cellValue)
	}, [cellValue])

	const showTooltip = (textAreaValue?.length || 0) > MIN_TOOLTIP_LENGTH

	const textPreview = useMemo(
		() => (textAreaValue ? <MultilinePreview value={textAreaValue} /> : null),
		[textAreaValue],
	)

	return (
		<>
			<CellTooltip
				title={showTooltip ? textAreaValue : undefined}
				isMenuOpen={isModalOpen}
				customContent={showTooltip ? textPreview : undefined}
				isEditMode={!isDisabled}
			>
				<input
					value={textAreaValue}
					style={{
						border: 'none',
						backgroundColor: 'transparent',
					}}
					className={className}
					onFocus={() => setModalOpen(true)}
					readOnly
				/>
			</CellTooltip>

			<ModalForm
				header={column?.header}
				open={isModalOpen}
				onClose={() => setModalOpen(false)}
				onSubmit={onSubmit}
				hasSubmitButton={!isDisabled}
				cancelTitle={t('CANCEL')}
				footerBefore={<CopyTextButton text={textAreaValue} />}
			>
				<TextArea
					onChange={(value) => setTextAreaValue(value as string)}
					disabled={isDisabled}
					className="textInputWithoutBorder"
					value={textAreaValue}
					autoFocus={true}
					onKeyDown={onKeyDown}
					rows={17}
				/>
			</ModalForm>
		</>
	)
}
